import { Injectable, WritableSignal, signal } from '@angular/core'
import { Observable } from 'rxjs'
import { ApiService } from './api.service'
import { AbstractControl, Validators } from '@angular/forms'
import { FormGroup, ValidatorFn } from '@angular/forms'
import { DateTime } from 'luxon'
import { CreditCardValidators } from 'angular-cc-library'
import {
	AdditionalItemsPayload,
	AuthNetResponse,
	CreditCard,
	Merchant,
	PaymentMethodCC,
	PaymentMethodProfile,
	PaymentProfile,
	UpsellPayload
} from 'src/types/billing'

declare var Accept: any

@Injectable({
	providedIn: 'root'
})

export class BillingService extends ApiService {
	public paymentCompleted: WritableSignal<boolean> = signal(false)

	public getPaymentProfiles(): Observable<PaymentProfile[]> {
		return this.getRequest('user/billing/payment-profiles')
	}

	public upgrade(payload: UpsellPayload, payment_method: PaymentMethodCC | PaymentMethodProfile, order_uuid: string) {
		return this.postRequest('order/invoicing/new-invoice', {
			applications: [payload],
			payment_method,
			currency: 'USD',
			order_uuid,
			originator: 'client'
		})
	}

	public purchaseProduct(payload: AdditionalItemsPayload[], payment_method: PaymentMethodCC | PaymentMethodProfile, order_uuid: string) {
		return this.postRequest('order/invoicing/new-invoice', {
			order_uuid,
			payment_method,
			currency: 'USD',
			originator: 'client',
			travelers: payload
		})
	}

	public getMerchantToken(source: string) {
		return this.postRequest(`order/get/token`, {domain: source, currency: 'USD'})
	}

	public getNonce(card: CreditCard, merchant: Merchant): Promise<any> {
		const secureData = {
			authData: {
				clientKey: merchant.token,
				apiLoginID: merchant.login_id,
			},
			cardData: {
				cardNumber: card.number.replace(/\s/g, ''),
				month: String(card.month),
				year: DateTime.fromISO(card.year).toFormat('yy'),
				cardCode: card.cvc,
				zip: card.zip
			},
		}

		return new Promise((resolve, reject) => 
			Accept.dispatchData(secureData, (response: AuthNetResponse) => {
				if (response.messages && response.messages.resultCode === 'Error') {
					reject(response.messages.message[0].text)
				}

				resolve(response.opaqueData)
			}))
	}

	public getCreditCardForm(): FormGroup {
		const fb = this.formBuilder.nonNullable
	
		const expirationValidator: ValidatorFn = (control: AbstractControl) => {
		const formGroup = control as FormGroup
		let monthControl = formGroup.get('month')!
			let yearControl = formGroup.get('year')!
	
			let month = monthControl.value
			let year = yearControl.value
	
			if(!monthControl || !yearControl) return null
			if(!month || !year) return null
	
			if(year > DateTime.now().year) {
				yearControl.updateValueAndValidity({onlySelf: true})
				monthControl.updateValueAndValidity({onlySelf: true})
				return null
			}
	
			if(month <= DateTime.now().month && year <= DateTime.now().year) {
				yearControl.setErrors({ expiration: true }); 
				monthControl.setErrors({ expiration: true })
			}
	
			if(month > DateTime.now().month && year <= DateTime.now().year){
				yearControl.updateValueAndValidity({onlySelf: true})
				monthControl.updateValueAndValidity({onlySelf: true})
				return null
			}
			
			return null
		}
	
		return fb.group({
				name: fb.control('', [
					Validators.required,
					Validators.pattern(/^([a-zA-Z\u0080-\u024F]+(?:(\. )|-| |'))*[a-zA-Z\u0080-\u024F]*$/)
				]),
				number: fb.control('', [Validators.required, CreditCardValidators.validateCCNumber]),
				month: fb.control('', [Validators.required]),
				year: fb.control('', [Validators.required]),
				cvc: fb.control('', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]),
				zip: fb.control('', [Validators.required, Validators.pattern(/\d{5}/)])
		}, {
			validators: expirationValidator,
		})
	}
}


